import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { api } from "../lib/api";

// M3X.COMP F.C.1 + F.C.2 — per-employee comp history timeline + analytics.
// Timeline newest-first; analytics panel below.
export function EmployeeCompHistoryPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const { id } = useParams<{ id: string }>();

  const q = useQuery({
    queryKey: ["comp-history", id],
    queryFn: () => api.getEmployeeCompHistory(id!),
    enabled: !!id,
  });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorPanel message={(q.error as Error).message} />;

  const { timeline, analytics } = q.data!;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("comp_history.title")}
        subtitle={t("comp_history.subtitle")}
      />

      {/* Aggregate analytics — F.C.2 */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <AnalyticsCard
          label={t("comp_history.current_salary")}
          value={
            analytics.current_basic_salary
              ? `${analytics.current_basic_salary} SAR`
              : "—"
          }
        />
        <AnalyticsCard
          label={t("comp_history.cumulative_growth")}
          value={
            analytics.cumulative_growth_pct
              ? `${analytics.cumulative_growth_pct}%`
              : "—"
          }
          tone={
            analytics.cumulative_growth_pct &&
            Number(analytics.cumulative_growth_pct) > 0
              ? "positive"
              : undefined
          }
        />
        <AnalyticsCard
          label={t("comp_history.yoy_cagr")}
          value={
            analytics.yoy_cagr_pct ? `${analytics.yoy_cagr_pct}%` : "—"
          }
        />
        <AnalyticsCard
          label={t("comp_history.compa_ratio")}
          value={
            analytics.current_compa_ratio
              ? Number(analytics.current_compa_ratio).toFixed(2)
              : "—"
          }
          tone={
            analytics.current_compa_ratio &&
            Number(analytics.current_compa_ratio) < 0.9
              ? "warning"
              : undefined
          }
        />
        <AnalyticsCard
          label={t("comp_history.tenure")}
          value={
            analytics.tenure_years
              ? `${analytics.tenure_years} ${t("comp_history.years")}`
              : "—"
          }
        />
        <AnalyticsCard
          label={t("comp_history.last_change")}
          value={
            analytics.last_change_date
              ? `${analytics.last_change_date} (${analytics.days_since_last_change}d)`
              : "—"
          }
        />
      </div>

      {/* Timeline — F.C.1 */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-700">
          <TrendingUp className="h-3 w-3" />
          {t("comp_history.timeline")}
        </div>
        {timeline.length === 0 ? (
          <EmptyState
            headline={t("comp_history.empty_title")}
            description={t("comp_history.empty_desc")}
          />
        ) : (
          <ol className="space-y-2">
            {timeline.map((entry) => {
              const positive =
                entry.delta_pct !== null && Number(entry.delta_pct) >= 0;
              const headline = isAr ? entry.headline_ar : entry.headline_en;
              return (
                <li
                  key={entry.entry_id}
                  className="flex items-start gap-3 rounded border border-slate-200 bg-white p-3"
                >
                  <div
                    className={`mt-1 flex h-7 w-7 items-center justify-center rounded-full ${
                      positive
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-rose-100 text-rose-700"
                    }`}
                  >
                    {positive ? (
                      <ArrowUp className="h-3 w-3" />
                    ) : (
                      <ArrowDown className="h-3 w-3" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{headline}</div>
                    <div className="text-xs text-slate-500">
                      {entry.effective_date}
                      {entry.workflow_state && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5">
                          {entry.workflow_state}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <Link
        to={`/people/employees/${id}`}
        className="text-xs text-slate-600 hover:underline"
      >
        ← {t("common.back")}
      </Link>
    </div>
  );
}

function AnalyticsCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "warning";
}) {
  const toneClass =
    tone === "positive"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50"
        : "border-slate-200 bg-white";
  return (
    <div className={`rounded border p-3 ${toneClass}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-base font-medium">{value}</div>
    </div>
  );
}
